import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import type { QuoteDocument } from "@kekkle/shared/types/documents/quote-document/quote-document";
import {
  subscribeToAllQuotes,
  subscribeToQuoteChanges,
  countQuotes,
  createQuoteId,
  addQuote as addQuoteService,
  updateQuote as updateQuoteService,
  deleteQuote as deleteQuoteService,
  reportQuote as reportQuoteService,
} from "@/helpers/firestore/QuoteService";
import { useQuoteFilterStore } from "./quotes-filter";
import { useUserStore } from "@/stores/modules/user/user";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useDeltaCollection } from "@/composables/useDeltaCollection";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import { setErrorToast } from "@/helpers/notifications/toast";
import type { FirestoreError } from "firebase/firestore";
import type { CacheSnapshot } from "vue-virtual-scroller";

type QuoteMemory = {
  yearsAgo: number;
  date: Date;
  count: number;
};

/**
 * Quotes store for managing quote documents and memories per group.
 * Delta-syncs with Firestore (see useDeltaCollection): one full read per
 * device, after that only quotes whose updated_at changed.
 * All filtering (author, date, text search) is done client-side via computed properties.
 * Only one active listener at a time (current group).
 */
export const useQuotesStore = defineStore("quotes", () => {
  const userStore = useUserStore();
  const filterStore = useQuoteFilterStore();

  const fetchError = ref<string | null>(null);

  /* State - organized by group ID */
  const sync = useDeltaCollection<QuoteDocument>({
    cacheName: "quotes",
    subscribeToAll: subscribeToAllQuotes,
    subscribeToChanges: subscribeToQuoteChanges,
    countFromServer: countQuotes,
    onError: (error) => {
      fetchError.value = handleFirestoreException({ e: error });
    },
  });
  const {
    documentsByGroup: quotesByGroup,
    loadingByGroup,
    isStaleByGroup,
  } = sync;

  const scrollIndex = ref(0);
  const scrollOffset = ref(0);
  let scrollerCache: CacheSnapshot | null = null;
  const selectedQuote = ref<QuoteDocument | Record<string, never>>({});

  /* Computed / Getters - for active group */
  const activeGroupId = computed(() => userStore.activeGroup);

  /** All quotes for the active group (unfiltered, from onSnapshot) */
  const allQuotes = computed(
    () =>
      (activeGroupId.value && quotesByGroup.value[activeGroupId.value]) || [],
  );

  /** Filtered quotes - what the view consumes */
  const quotes = computed(() => {
    const params = filterStore.getFilterParams();
    let result = allQuotes.value;

    if (params.authorUuid) {
      result = result.filter((q) =>
        q.authors?.includes(params.authorUuid as string),
      );
    }

    if (params.date) {
      const filterDate = params.date;
      result = result.filter((q) => isSameDay(q.timestamp, filterDate));
    }

    if (params.searchText) {
      const term = params.searchText.toLowerCase();
      result = result.filter(
        (q) =>
          q.quote.toLowerCase().includes(term) ||
          q.context?.toLowerCase().includes(term),
      );
    }

    return result;
  });

  const loadingInitialQuotes = computed(() => {
    if (!activeGroupId.value) return false;
    return loadingByGroup.value[activeGroupId.value] ?? false;
  });

  const isStale = computed(() => {
    if (!activeGroupId.value) return false;
    return isStaleByGroup.value[activeGroupId.value] ?? false;
  });

  /** Quotes from this day in previous years (derived from allQuotes) */
  const quoteMemories = computed(() => {
    if (allQuotes.value.length === 0) return [];

    const oldestQuote = allQuotes.value[allQuotes.value.length - 1]!;
    const today = new Date();
    const currentYear = today.getFullYear();
    const maxYearsBack = Math.min(
      10,
      currentYear - oldestQuote.timestamp.getFullYear(),
    );

    const memories: QuoteMemory[] = [];
    for (let i = 1; i <= maxYearsBack; i++) {
      const dateToCheck = new Date(today);
      dateToCheck.setFullYear(currentYear - i);

      const count = allQuotes.value.filter((q) =>
        isSameDay(q.timestamp, dateToCheck),
      ).length;

      if (count > 0) {
        memories.push({ yearsAgo: i, date: dateToCheck, count });
      }
    }
    return memories;
  });

  const selectedQuoteData = computed(
    () => selectedQuote.value as QuoteDocument,
  );

  const hasSelectedQuote = computed(
    () => Object.keys(selectedQuote.value).length > 0,
  );

  /* Helper functions */
  function isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  /* Listener lifecycle */
  function subscribe(groupId: string): void {
    if (sync.subscribe(groupId)) {
      fetchError.value = null;
    }
  }

  function unsubscribe(): void {
    sync.unsubscribe();
  }

  /* Actions */
  function setSelectedQuote(quoteId: string): void {
    const found = allQuotes.value.find((quote) => quote._id === quoteId);
    selectedQuote.value = found || {};
  }

  function setScrollState(
    index: number,
    offset: number,
    cache: CacheSnapshot | null,
  ): void {
    scrollIndex.value = index;
    scrollOffset.value = offset;
    scrollerCache = cache;
  }

  function reset(): void {
    sync.reset();
  }

  function findQuote(
    groupId: string,
    quoteId: string,
  ): QuoteDocument | undefined {
    return quotesByGroup.value[groupId]?.find((quote) => quote._id === quoteId);
  }

  /** Subscribe to quotes for the active group */
  function subscribeToQuotes(): void {
    const groupId = activeGroupId.value;
    if (!groupId) return;
    subscribe(groupId);
  }

  async function addQuote(data: Omit<QuoteDocument, "_id">): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    try {
      const quote: QuoteDocument = { ...data, _id: createQuoteId(groupId) };
      await sync.writeLocally(groupId, quote, () =>
        addQuoteService(groupId, quote),
      );
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    }
  }

  async function updateQuote(
    quoteId: string,
    data: Partial<Omit<QuoteDocument, "_id">>,
  ): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    try {
      const existing = findQuote(groupId, quoteId);
      const write = () => updateQuoteService(groupId, quoteId, data);
      await (existing
        ? sync.writeLocally(groupId, { ...existing, ...data }, write)
        : write());

      // Update selected quote if it's the one being edited
      if (selectedQuote.value._id === quoteId) {
        const current = selectedQuote.value as QuoteDocument;
        Object.assign(current, data);
      }
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    }
  }

  async function deleteQuote(quoteId: string): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    try {
      await deleteQuoteService(groupId, quoteId);
      // Deletes never reach the delta listener; drop it locally so the
      // next count check does not trigger a full resync.
      sync.removeLocally(groupId, quoteId);

      // Clear selected quote if it was the deleted one
      if (selectedQuote.value._id === quoteId) {
        selectedQuote.value = {};
      }
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    }
  }

  async function reportQuote(quoteId: string): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    const authStore = useAuthStore();
    const reporterId = authStore.authUuid;

    if (!reporterId) {
      throw new Error("User must be authenticated to report quotes");
    }

    const existing = findQuote(groupId, quoteId);
    const write = () => reportQuoteService(groupId, quoteId, reporterId);
    await (existing
      ? sync.writeLocally(
          groupId,
          {
            ...existing,
            reported_by: [...new Set([...existing.reported_by, reporterId])],
          },
          write,
        )
      : write());
  }

  /* Watch active group changes */
  watch(
    () => activeGroupId.value,
    (newGroupId, oldGroupId) => {
      if (newGroupId && newGroupId !== oldGroupId) {
        subscribe(newGroupId);
      }
    },
  );

  /* Exposed store */
  return {
    // State
    loadingInitialQuotes,
    isStale,
    isStaleByGroup,
    scrollIndex,
    scrollOffset,
    scrollerCache,
    quotes,
    allQuotes,
    selectedQuote,
    fetchError,
    quoteMemories,
    hasSelectedQuote,
    selectedQuoteData,

    // Actions
    setSelectedQuote,
    setScrollState,
    reset,
    subscribeToQuotes,
    addQuote,
    updateQuote,
    deleteQuote,
    reportQuote,
    unsubscribe,
  };
});
